import { z } from 'zod';

import { jsonSchema, type JsonValue } from './json';

// Shared domain types for rules used across frontend/backend.
export interface Rule {
  id: string;
  ruleSetID: string;
  name: string;
  version: number;
  active: boolean;
  body: Record<string, unknown>;
  siteID: string;
  createdBy: string;
  lastUpdatedBy: string;
  createdAt: string;
  lastUpdatedAt: string | null;
  status: RuleStatus;
}

export type RuleViolation = z.infer<typeof ruleViolationSchema>;
export type RuleViolationSeverity = RuleViolation['severity'];
export type RuleActionChange = z.infer<typeof ruleActionChangeSchema>;
export type AppliedRuleAction = z.infer<typeof appliedRuleActionSchema>;
export type SuggestedRuleAction = z.infer<typeof suggestedRuleActionSchema>;
export type RuleEvaluationResult = z.infer<typeof ruleEvaluationResultSchema>;

export type CreateRuleInput = Omit<
  Rule,
  'id' | 'version' | 'lastUpdatedBy' | 'createdAt' | 'lastUpdatedAt'
>;

export type EditRuleInput = {
  ruleSetID: Rule['ruleSetID'];
  lastUpdatedBy: Rule['lastUpdatedBy'];
  name?: Rule['name'];
  body?: Rule['body'];
  active?: Rule['active'];
  status?: Rule['status'];
};

export type RuleApplyBehavior = z.infer<typeof ruleApplyBehaviorSchema>;
export type RuleApplyNotification = z.infer<typeof ruleApplyNotificationSchema>;
export type RuleApplyWritePolicy = z.infer<typeof ruleApplyWritePolicySchema>;
export type RuleApplyMetadata = z.infer<typeof ruleApplyMetadataSchema>;

export type RuleStatus = z.infer<typeof ruleStatusSchema>;

export type TariffBillingHistoryWindow = z.infer<
  typeof tariffBillingHistoryWindowSchema
>;

export type TariffBillingHistoryParams = z.infer<
  typeof tariffBillingHistoryParamsSchema
>;

export type RuleCriteriaGroup = z.infer<typeof ruleCriteriaGroupSchema>;

export type GroupedRuleCriteria = z.infer<typeof groupedRuleCriteriaSchema>;

export type ClaimRuleEvaluationInput = z.infer<
  typeof claimRuleEvaluationInputSchema
>;

// Rule actions, criteria fields, and operators.

export enum ScopeMode {
  DuplicateClaim = 'duplicateClaim',
  TariffBillingHistory = 'tariffBillingHistory',
  FacilityNumber = 'facilityNumber',
  PatientCount = 'patientCount',
  VirtualPhysicianRegion = 'virtualPhysicianRegion',
  ProviderComments = 'providerComments',
  ClerkComments = 'clerkComments',
  InternalComments = 'internalComments',
  ProviderNotes = 'providerNotes',
  ServiceStart = 'serviceStart',
  ServiceEnd = 'serviceEnd',
  InterpretingRadiologist = 'interpretingRadiologist',
  OptometristReason = 'optometristReason',
  ThirdPartyLiability = 'thirdPartyLiability',
  LocationOfService = 'locationOfService',
  ServiceLocationIndicator = 'serviceLocationIndicator',
  ReferringProvider = 'referringProvider',
  HospitalCode = 'hospitalCode',
  AnyClaim = 'anyClaim',
  PatientAge = 'patientAge',
  PatientProvince = 'patientProvince',
  PatientSex = 'patientSex',
  PatientMaritalStatus = 'patientMaritalStatus',
  ServiceStartTimeLocal = 'serviceStartTimeLocal',
  ServiceStartDayLocal = 'serviceStartDayLocal',
  ExactTariff = 'exactTariff',
  TariffNotEqual = 'tariffNotEqual',
  TariffCode = 'tariffCode',
  TariffPrefix = 'tariffPrefix',
  SpecialtyCode = 'specialtyCode',
  TariffHasAnestheticUnitValue = 'tariffHasAnestheticUnitValue',
}

export enum PatientAgeOperator {
  Equal = 'equal',
  LessThan = 'lessThan',
  LessThanInclusive = 'lessThanInclusive',
  GreaterThan = 'greaterThan',
  GreaterThanInclusive = 'greaterThanInclusive',
  BetweenInclusive = 'betweenInclusive',
}

export enum AssertionField {
  DuplicateClaim = 'duplicateClaim',
  TariffBillingHistory = 'tariffBillingHistory',
  PatientAge = 'patientAgeAtServiceStart',
  PatientProvince = 'patientProvince',
  PatientSex = 'patientSex',
  PatientMaritalStatus = 'patientMaritalStatus',
  ReferringProvider = 'referringProvider',
  ProviderComments = 'providerComments',
  InternalComments = 'internalComments',
  ProviderNotes = 'providerNotes',
  InterpretingRadiologist = 'interpretingRadiologist',
  VirtualPhysicianRegion = 'virtualPhysicianRegion',
  OptometristReason = 'optometristReason',
  ThirdPartyLiability = 'thirdPartyLiability',
  Incorporated = 'incorporated',
  ConfidentialCode = 'confidentialCode',
  ChiroService = 'chiroService',
  PatientCount = 'patientCount',
  HospitalName = 'hospitalName',
  FacilityNumber = 'facilityNumber',
  ServiceStart = 'serviceStart',
  ServiceEnd = 'serviceEnd',
  ClaimServiceLocationIndicator = 'claimServiceLocationIndicator',
  ClaimLocationOfService = 'claimLocationOfService',
  ClaimPatientName = 'claimPatientName',
  ClaimHospitalCode = 'claimHospitalCode',
  ClaimClerkComments = 'claimClerkComments',
  ClaimServiceStartMinutesLocal = 'claimServiceStartMinutesLocal',
  ClaimServiceStartDayOfWeekLocal = 'claimServiceStartDayOfWeekLocal',
}

export enum AssertionOperator {
  Equals = 'equals',
  NotEquals = 'notEquals',
  Contains = 'contains',
  LessThan = 'lessThan',
  LessThanInclusive = 'lessThanInclusive',
  GreaterThan = 'greaterThan',
  GreaterThanInclusive = 'greaterThanInclusive',
  In = 'in',
  NotIn = 'notIn',
  MustExist = 'mustExist',
  BetweenInclusive = 'betweenInclusive',
  NotBetweenInclusive = 'notBetweenInclusive',
  DateOnOrAfter = 'dateOnOrAfter',
  DateOnOrBefore = 'dateOnOrBefore',
}

export enum RuleSeverity {
  Alert = 'alert',
  Block = 'block',
}

export enum RuleMode {
  Validate = 'validate',
  ApplyData = 'applyData',
  TariffTransforms = 'tariffTransforms',
}

export enum WhenLogic {
  All = 'all',
  Any = 'any',
}

export enum ServiceStartTimeOperator {
  Equal = 'equal',
  BetweenInclusive = 'betweenInclusive',
  NotBetweenInclusive = 'notBetweenInclusive',
}

export enum ServiceStartDayOperator {
  Equal = 'equal',
  BetweenInclusive = 'betweenInclusive',
  NotBetweenInclusive = 'notBetweenInclusive',
}

export enum ServiceStartDayValue {
  Sunday = '0',
  Monday = '1',
  Tuesday = '2',
  Wednesday = '3',
  Thursday = '4',
  Friday = '5',
  Saturday = '6',
}

export enum ApplyField {
  ReferringProvider = 'referringProvider',
  ProviderComments = 'providerComments',
  ClerkComments = 'clerkComments',
  InternalComments = 'internalComments',
  ProviderNotes = 'providerNotes',
  ServiceLocationIndicator = 'serviceLocationIndicator',
  LocationOfService = 'locationOfService',
  HospitalCode = 'hospitalCode',
  HospitalName = 'hospitalName',
  FacilityNumber = 'facilityNumber',
  InterpretingRadiologist = 'interpretingRadiologist',
  VirtualPhysicianRegion = 'virtualPhysicianRegion',
  OptometristReason = 'optometristReason',
  ThirdPartyLiability = 'thirdPartyLiability',
  Incorporated = 'incorporated',
  ConfidentialCode = 'confidentialCode',
  ChiroService = 'chiroService',
  PatientCount = 'patientCount',
  ServiceStart = 'serviceStart',
  ServiceEnd = 'serviceEnd',
  TariffCode = 'tariffCode',
  TimeBasedServiceUnits = 'timeBasedServiceUnits',
  TimeBasedAnesthesiaUnits = 'timeBasedAnesthesiaUnits',
}

export enum TimeBasedIntervalMinutes {
  Fifteen = 15,
  Thirty = 30,
  FortyFive = 45,
  Sixty = 60,
}

export const ruleApplyBehaviorSchema = z.enum(['automatic', 'suggest']);
export const ruleApplyNotificationSchema = z.enum(['notice', 'silent']);
export const ruleApplyWritePolicySchema = z.enum(['blank', 'replace']);

export const ruleApplyMetadataSchema = z.object({
  behavior: ruleApplyBehaviorSchema.optional(),
  // Suggestions remain visible; this preference controls automatic-update notices.
  notification: ruleApplyNotificationSchema.optional(),
  writePolicy: ruleApplyWritePolicySchema.optional(),
});

// Persisted rules and create/edit inputs.

export const ruleStatusSchema = z.enum(['draft', 'published', 'archived']);

const isJsonObject = (value: JsonValue): value is Record<string, JsonValue> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const hasExecutableRuleShape = (body: Record<string, JsonValue>): boolean =>
  isJsonObject(body.conditions) &&
  isJsonObject(body.event) &&
  typeof body.event.type === 'string' &&
  body.event.type.trim().length > 0;

const validateApplyMetadata = (
  value: Record<string, JsonValue>,
  path: (string | number)[],
  context: z.RefinementCtx
): void => {
  const result = ruleApplyMetadataSchema.safeParse(value);
  if (result.success) return;
  for (const issue of result.error.issues) {
    context.addIssue({ ...issue, path: [...path, ...issue.path] });
  }
};

const validateRuleBodyApplyMetadata = (
  body: Record<string, JsonValue>,
  context: z.RefinementCtx
): void => {
  if (!isJsonObject(body.event)) return;
  const params = body.event.params;
  if (!isJsonObject(params) || params.action !== 'applyData') return;
  const apply = params.apply;
  if (!isJsonObject(apply)) return;

  const path = ['event', 'params', 'apply'];
  validateApplyMetadata(apply, path, context);
  if (!Array.isArray(apply.updates)) return;
  apply.updates.forEach((update, index) => {
    if (isJsonObject(update)) {
      validateApplyMetadata(update, [...path, 'updates', index], context);
    }
  });
};

export const ruleBodySchema: z.ZodType<Record<string, JsonValue>> = z
  .record(z.string(), jsonSchema)
  .refine(hasExecutableRuleShape, {
    message:
      'Rule body must be a json-rules-engine rule object with conditions and event.type.',
  })
  .superRefine(validateRuleBodyApplyMetadata);

export const ruleSchema = z.object({
  id: z.guid('Rule ID must be a valid UUID.'),
  ruleSetID: z.guid('Rule set ID must be a valid UUID.'),
  name: z
    .string()
    .trim()
    .min(1, 'Rule name cannot be empty.')
    .max(255, 'Rule name must be 255 characters or fewer.'),
  version: z.number().int().min(1).default(1),
  active: z.boolean().default(false),
  body: ruleBodySchema,
  siteID: z.guid('Site ID must be a valid UUID.'),
  createdBy: z.guid('Created by ID must be a valid UUID.'),
  lastUpdatedBy: z.guid('Last updated by ID must be a valid UUID.'),
  createdAt: z.coerce.date(),
  lastUpdatedAt: z.coerce.date().nullable(),
  status: ruleStatusSchema.default('draft'),
});

export const isRule = (obj: unknown): obj is Rule => {
  return ruleSchema.safeParse(obj).success;
};

// Tariff fact parameters.

export const tariffBillingHistoryWindowSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('calendarDay') }).strict(),
  z.object({ type: z.literal('calendarWeek') }).strict(),
  z.object({ type: z.literal('calendarYear') }).strict(),
  z.object({ type: z.literal('anyTime') }).strict(),
  z
    .object({
      type: z.literal('rolling'),
      amount: z.number().int().positive(),
      unit: z.enum(['days', 'weeks', 'months']),
    })
    .strict(),
]);

export const tariffBillingHistoryParamsSchema = z
  .object({
    tariffCodes: z
      .array(
        z
          .string()
          .trim()
          .regex(/^\d{5,}$/)
      )
      .min(1),
    window: tariffBillingHistoryWindowSchema,
    sameProvider: z.boolean(),
  })
  .strict();

export const tariffExclusionsSchema = z.array(z.string().min(1)).optional();

/** Positive tariff-row conditions that support full-tariff exclusions. */
export const supportsTariffExclusions = (
  scopeMode: ScopeMode,
  whenOperator: string
): boolean =>
  whenOperator === 'is' &&
  [
    ScopeMode.TariffPrefix,
    ScopeMode.TariffCode,
    ScopeMode.SpecialtyCode,
    ScopeMode.TariffHasAnestheticUnitValue,
  ].includes(scopeMode);

// Editor defaults and persisted criteria metadata.

export const whenOperatorSchema = z.enum([
  'is',
  'isNot',
  'exists',
  'notExists',
  'contains',
  'notContains',
  'lessThan',
  'lessThanInclusive',
  'greaterThan',
  'greaterThanInclusive',
  'between',
  'notBetween',
  'onOrAfter',
  'onOrBefore',
]);

export const billingHistoryStateSchema = z.object({
  windowType: z
    .enum(
      tariffBillingHistoryWindowSchema.options.map(
        (window) => window.shape.type.value
      )
    )
    .catch('calendarDay'),
  amount: z.string().catch('1'),
  unit: z.enum(['days', 'weeks', 'months']).catch('days'),
  sameProvider: z.boolean().catch(true),
});

export const whenClauseSchema = z.object({
  id: z.number().catch(1),
  scopeMode: z.enum(ScopeMode).catch(ScopeMode.AnyClaim),
  whenOperator: whenOperatorSchema.catch('is'),
  scopeValuesText: z.string().catch(''),
  excludedTariffValuesText: z.string().catch(''),
  billingHistory: billingHistoryStateSchema.catch(
    billingHistoryStateSchema.parse({})
  ),
  // Saved checks are OR-ed together and AND-ed with the regular criteria.
  legacyCheckGroup: z.boolean().catch(false),
  serviceStartTimeOperator: z
    .enum(ServiceStartTimeOperator)
    .catch(ServiceStartTimeOperator.Equal),
  serviceStartTimeValue: z.string().catch(''),
  serviceStartTimeStart: z.string().catch(''),
  serviceStartTimeEnd: z.string().catch(''),
  serviceStartDayOperator: z
    .enum(ServiceStartDayOperator)
    .catch(ServiceStartDayOperator.Equal),
  serviceStartDayValue: z
    .enum(ServiceStartDayValue)
    .catch(ServiceStartDayValue.Sunday),
  serviceStartDayStart: z
    .enum(ServiceStartDayValue)
    .catch(ServiceStartDayValue.Monday),
  serviceStartDayEnd: z
    .enum(ServiceStartDayValue)
    .catch(ServiceStartDayValue.Friday),
  patientAgeOperator: z
    .enum(PatientAgeOperator)
    .catch(PatientAgeOperator.Equal),
  patientAgeValue: z.string().catch(''),
  patientAgeStart: z.string().catch(''),
  patientAgeEnd: z.string().catch(''),
});

const persistedBillingHistorySchema = z
  .looseObject({
    windowType: billingHistoryStateSchema.shape.windowType.unwrap().optional(),
    amount: billingHistoryStateSchema.shape.amount.unwrap().optional(),
    unit: billingHistoryStateSchema.shape.unit.unwrap().optional(),
    sameProvider: billingHistoryStateSchema.shape.sameProvider
      .unwrap()
      .optional(),
  })
  .transform((history) => billingHistoryStateSchema.parse(history));

// Missing optional inputs receive editor defaults; invalid saved values must remain in JSON mode.
export const ruleCriteriaClauseSchema = whenClauseSchema
  .loose()
  .extend({
    id: z.number().int().positive(),
    scopeMode: z
      .enum(ScopeMode)
      .refine((scopeMode) => scopeMode !== ScopeMode.TariffNotEqual),
    whenOperator: whenOperatorSchema,
    scopeValuesText: whenClauseSchema.shape.scopeValuesText.unwrap().optional(),
    excludedTariffValuesText: whenClauseSchema.shape.excludedTariffValuesText
      .unwrap()
      .optional(),
    billingHistory: persistedBillingHistorySchema.optional(),
    legacyCheckGroup: whenClauseSchema.shape.legacyCheckGroup
      .unwrap()
      .optional(),
    serviceStartTimeOperator: whenClauseSchema.shape.serviceStartTimeOperator
      .unwrap()
      .optional(),
    serviceStartTimeValue: whenClauseSchema.shape.serviceStartTimeValue
      .unwrap()
      .optional(),
    serviceStartTimeStart: whenClauseSchema.shape.serviceStartTimeStart
      .unwrap()
      .optional(),
    serviceStartTimeEnd: whenClauseSchema.shape.serviceStartTimeEnd
      .unwrap()
      .optional(),
    serviceStartDayOperator: whenClauseSchema.shape.serviceStartDayOperator
      .unwrap()
      .optional(),
    serviceStartDayValue: whenClauseSchema.shape.serviceStartDayValue
      .unwrap()
      .optional(),
    serviceStartDayStart: whenClauseSchema.shape.serviceStartDayStart
      .unwrap()
      .optional(),
    serviceStartDayEnd: whenClauseSchema.shape.serviceStartDayEnd
      .unwrap()
      .optional(),
    patientAgeOperator: whenClauseSchema.shape.patientAgeOperator
      .unwrap()
      .optional(),
    patientAgeValue: whenClauseSchema.shape.patientAgeValue.unwrap().optional(),
    patientAgeStart: whenClauseSchema.shape.patientAgeStart.unwrap().optional(),
    patientAgeEnd: whenClauseSchema.shape.patientAgeEnd.unwrap().optional(),
  })
  .refine(
    (clause) =>
      !clause.excludedTariffValuesText?.trim() ||
      supportsTariffExclusions(clause.scopeMode, clause.whenOperator),
    {
      message:
        'Tariff exclusions require a supported positive tariff condition.',
    }
  )
  .transform(
    (clause): z.infer<typeof whenClauseSchema> => ({
      ...clause,
      ...whenClauseSchema.parse(clause),
    })
  );

export const ruleCriteriaGroupSchema = z.looseObject({
  id: z.number().int().positive(),
  operator: z.enum(WhenLogic),
  clauses: z.array(ruleCriteriaClauseSchema).min(1),
});

export const groupedRuleCriteriaSchema = z
  .looseObject({
    operator: z.enum(WhenLogic),
    groups: z.array(ruleCriteriaGroupSchema).min(1),
  })
  .refine(
    (criteria) => {
      const groupIDs = criteria.groups.map((group) => group.id);
      const clauseIDs = criteria.groups.flatMap((group) =>
        group.clauses.map((clause) => clause.id)
      );
      return (
        new Set(groupIDs).size === groupIDs.length &&
        new Set(clauseIDs).size === clauseIDs.length
      );
    },
    { message: 'Group and condition IDs must be unique.' }
  );

export const hasGroupedRuleCriteria = (body: unknown): boolean => {
  if (!body || typeof body !== 'object' || !('when' in body)) return false;
  const when = body.when;
  return Boolean(when && typeof when === 'object' && 'groups' in when);
};

// Claim evaluation requests and results.

export const ruleViolationSchema = z
  .object({
    severity: z.enum(['block', 'warning']),
    issueID: z.string().optional(),
    ruleSetID: z.string().optional(),
    ruleSetName: z.string().optional(),
    ruleName: z.string().optional(),
    category: z.string().optional(),
    code: z.string(),
    message: z.string(),
    formFields: z.array(z.string()).optional(),
  })
  .strict();

export const ruleActionChangeSchema = z
  .object({
    field: z.string(),
    value: z.unknown(),
    previousValue: z.unknown(),
  })
  .strict();

export const appliedRuleActionSchema = z
  .object({
    ...ruleApplyMetadataSchema.shape,
    ruleSetID: z.string(),
    ruleSetName: z.string(),
    ruleName: z.string(),
    field: z.string(),
    value: z.unknown(),
    priority: z.number(),
    claimIndex: z.number().int().nonnegative().optional(),
    ruleVersion: z.number().optional(),
    actionID: z.string().optional(),
    previousValue: z.unknown().optional(),
    changes: z.array(ruleActionChangeSchema).optional(),
  })
  .strict();

export const suggestedRuleActionSchema = appliedRuleActionSchema.extend({
  behavior: z.literal('suggest'),
  suggestionID: z.string(),
  changes: z.array(ruleActionChangeSchema),
});

export const ruleEvaluationResultSchema = z
  .object({
    passed: z.boolean(),
    violations: z.array(ruleViolationSchema),
    appliedData: z.record(z.string(), z.unknown()),
    appliedActions: z.array(appliedRuleActionSchema),
    suggestedActions: z.array(suggestedRuleActionSchema).optional(),
  })
  .strict();

export const claimRuleEvaluationInputSchema = z
  .object({
    siteID: z.guid('Site ID must be a valid UUID.'),
    claimID: z.guid('Claim ID must be a valid UUID.').optional(),
    claimData: z.record(z.string(), z.unknown()),
  })
  .strict();

// Rule facts and their corresponding claim form fields.

const ruleFactFormFields: Readonly<Record<string, readonly string[]>> = {
  [AssertionField.DuplicateClaim]: ['tariffs'],
  [AssertionField.TariffBillingHistory]: ['tariffs'],
  hasPriorDuplicateClaim: ['tariffs'],
  hasPriorTariffBilling: ['tariffs'],
  [AssertionField.PatientAge]: ['patient.dateOfBirth'],
  [AssertionField.PatientProvince]: ['patient.province'],
  [AssertionField.PatientSex]: ['patient.sex'],
  [AssertionField.ReferringProvider]: ['referringProvider'],
  [AssertionField.ProviderComments]: ['providerComments'],
  [AssertionField.InternalComments]: ['internalComments'],
  [AssertionField.ProviderNotes]: ['providerNotes'],
  [AssertionField.InterpretingRadiologist]: ['interpretingRadiologist'],
  [AssertionField.VirtualPhysicianRegion]: ['virtualPhysicianRegion'],
  [AssertionField.OptometristReason]: ['optometristReason'],
  [AssertionField.ThirdPartyLiability]: ['thirdPartyLiability'],
  [AssertionField.Incorporated]: ['incorporated'],
  [AssertionField.ChiroService]: ['chiroService'],
  [AssertionField.PatientCount]: ['patientCount'],
  [AssertionField.HospitalName]: ['hospitalCode'],
  [AssertionField.FacilityNumber]: ['facilityNumber'],
  [AssertionField.ServiceStart]: ['serviceStart'],
  [AssertionField.ServiceEnd]: ['serviceEnd'],
  [AssertionField.ClaimServiceLocationIndicator]: ['serviceLocationIndicator'],
  [AssertionField.ClaimLocationOfService]: ['locationOfService'],
  [AssertionField.ClaimPatientName]: ['patientID'],
  [AssertionField.ClaimHospitalCode]: ['hospitalCode'],
  [AssertionField.ClaimClerkComments]: ['clerkComments'],
  [AssertionField.ClaimServiceStartMinutesLocal]: ['serviceStart'],
  [AssertionField.ClaimServiceStartDayOfWeekLocal]: ['serviceStart'],
  hasTariffPrefix: ['tariffs'],
  hasTariffCode: ['tariffs'],
  hasTariff: ['tariffs'],
  tariffFullCodes: ['tariffs'],
  tariffSpecialtyCodes: ['tariffs'],
  hasTariffAnestheticUnitValue: ['tariffs'],
  patientMaritalStatus: ['patient.maritalStatus'],
  clerkComments: ['clerkComments'],
  serviceLocationIndicator: ['serviceLocationIndicator'],
  locationOfService: ['locationOfService'],
  hospitalCode: ['hospitalCode'],
  confidentialCode: ['confidentialCode'],
  claimServiceStartHourUTC: ['serviceStart'],
  claimServiceStartDayOfWeekUTC: ['serviceStart'],
  claimServiceStartIsWeekend: ['serviceStart'],
  claimServiceStartISO: ['serviceStart'],
  claimServiceStartMinutesUTC: ['serviceStart'],
  claimServiceStartIsWeekendLocal: ['serviceStart'],
  claimServiceEndHourUTC: ['serviceEnd'],
  claimServiceEndDayOfWeekUTC: ['serviceEnd'],
  claimServiceEndIsWeekend: ['serviceEnd'],
  claimServiceEndISO: ['serviceEnd'],
  claimServiceEndMinutesUTC: ['serviceEnd'],
  claimServiceEndMinutesLocal: ['serviceEnd'],
  claimServiceEndDayOfWeekLocal: ['serviceEnd'],
  claimServiceEndIsWeekendLocal: ['serviceEnd'],
};

export const getRuleFactFormFields = (fact: string): string[] => [
  ...(Object.hasOwn(ruleFactFormFields, fact) ? ruleFactFormFields[fact] : []),
];
